import type { ReactNode } from 'react'

/**
 * The small charts the dashboard is made of (D-138).
 *
 * Drawn by hand in SVG rather than with a charting library. Three of these — a line
 * against a target, a row of bars, a ring — are the whole requirement, and each is a
 * dozen lines of geometry; a library would add a few hundred kilobytes to the first
 * screen the shop sees in the morning to save them.
 *
 * Each one carries `role="img"` and an `aria-label` that reads the figures out, because
 * a chart nobody can hear is a chart half the staff cannot use. The plot is SVG; every
 * label is HTML beside it, so nothing is stretched out of shape when the box is.
 */

/** A nice round number at or above the top of the data, so the gridlines read evenly. */
function ceiling(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  return Math.ceil(value / (magnitude / 2)) * (magnitude / 2)
}

export type TrendPoint = { label: string; value: number; title?: string }

/**
 * A line across the period with the target, where there is one, dashed behind it.
 */
export function TrendChart({
  points,
  target,
  format,
  label,
  height = 180,
}: {
  points: TrendPoint[]
  /** The same figure for every point — a day's target, a week's. Null where none is set. */
  target?: number | null
  format: (value: number) => string
  /** What the whole chart is, for the reader who cannot see it. */
  label: string
  height?: number
}) {
  const top = ceiling(Math.max(...points.map((p) => p.value), target ?? 0))
  const lines = [top, top * 0.75, top * 0.5, top * 0.25, 0]
  const x = (i: number) => (points.length === 1 ? 50 : (i / (points.length - 1)) * 100)
  const y = (value: number) => 100 - (value / top) * 100
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.value)}`).join(' ')

  return (
    <div>
      <div className="flex gap-2">
        <div className="flex w-14 shrink-0 flex-col justify-between py-0.5 text-right text-[10px] tabular-nums text-dpl-muted" aria-hidden="true">
          {lines.map((value) => (
            <span key={value}>{compact(value)}</span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1" style={{ height }}>
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="h-full w-full"
            role="img"
            aria-label={`${label}: ${points.map((p) => `${p.label} ${format(p.value)}`).join(', ')}${target ? `, against a target of ${format(target)}` : ''}`}
          >
            {lines.map((value) => (
              <line key={value} x1="0" x2="100" y1={y(value)} y2={y(value)} stroke="currentColor" className="text-dpl-border" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {target ? (
              <line
                x1="0"
                x2="100"
                y1={y(target)}
                y2={y(target)}
                stroke="currentColor"
                className="text-dpl-success"
                strokeWidth="2"
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
            <path d={path} fill="none" stroke="currentColor" className="text-dpl-primary" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            {points.map((p, i) => (
              <circle key={p.label} cx={x(i)} cy={y(p.value)} r="3" className="fill-dpl-surface text-dpl-primary" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke">
                <title>{p.title ?? `${p.label}: ${format(p.value)}`}</title>
              </circle>
            ))}
          </svg>
        </div>
      </div>
      <div className="ml-16 flex justify-between gap-1 pt-2 text-[10px] text-dpl-muted" aria-hidden="true">
        {points.map((p, i) => (
          // On a month of days, every label would overlap the next; one in five is legible.
          <span key={p.label} className={points.length > 10 && i % 5 !== 0 ? 'invisible' : ''}>
            {p.label}
          </span>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-dpl-muted">
        <span className="flex items-center gap-1.5"><span className="h-0.5 w-5 rounded bg-dpl-primary" />Sales</span>
        {target ? <span className="flex items-center gap-1.5"><span className="h-0.5 w-5 rounded border-t-2 border-dashed border-dpl-success" />Target</span> : null}
      </div>
    </div>
  )
}

/** `124,560` as `125K` — for an axis, where the exact figure is on the point itself. */
function compact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${round(value / 1_000_000)}M`
  if (abs >= 1_000) return `${round(value / 1_000)}K`
  return round(value)
}

const round = (value: number) => Number(value.toFixed(value < 10 ? 1 : 0)).toLocaleString('en-KE')

/**
 * The shape of a run of figures, no bigger than a word (D-154). No axes, no labels: it says
 * "rising" or "falling" beside a number that says how much.
 */
export function Sparkline({ values, tone = 'primary', className = 'h-8 w-24' }: { values: number[]; tone?: 'primary' | 'success' | 'accent'; className?: string }) {
  if (values.length < 2) return <span className={className} aria-hidden="true" />

  const top = Math.max(...values)
  const floor = Math.min(...values)
  const span = top - floor || 1
  const x = (i: number) => (i / (values.length - 1)) * 100
  const y = (v: number) => 28 - ((v - floor) / span) * 24
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const colour = tone === 'success' ? 'text-dpl-success' : tone === 'accent' ? 'text-dpl-accent' : 'text-dpl-primary'

  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className={`${className} ${colour}`} aria-hidden="true">
      <path d={`${line} L100,30 L0,30 Z`} fill="currentColor" opacity="0.12" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export type Bar = { key: string; label: string; value: number; caption?: string }

/** One bar per row, each against the largest — how the branches compare at a glance. */
export function BarList({ bars, format, label }: { bars: Bar[]; format: (value: number) => string; label: string }) {
  const top = Math.max(...bars.map((b) => b.value), 1)

  return (
    <ul className="space-y-3" aria-label={label}>
      {bars.map((bar) => (
        <li key={bar.key} className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] items-center gap-3 sm:grid-cols-[9rem_minmax(0,1fr)_auto]">
          <span className="truncate text-sm text-dpl-text" title={bar.label}>{bar.label}</span>
          <span className="h-5 rounded bg-dpl-primary/10">
            <span className="block h-5 rounded bg-dpl-primary" style={{ width: `${Math.max(1.5, (bar.value / top) * 100)}%` }} />
          </span>
          <span className="whitespace-nowrap text-right text-sm tabular-nums text-dpl-text">{format(bar.value)}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * A ring filled to a percentage, with the figure in the middle and, where there is a
 * target, a tick on the ring showing where it sits.
 */
export function Donut({
  percent,
  target,
  label,
  caption,
  tone = 'success',
}: {
  percent: number
  target?: number | null
  label: string
  caption?: ReactNode
  tone?: 'success' | 'danger'
}) {
  const clamped = Math.max(0, Math.min(100, percent))
  const circumference = 2 * Math.PI * 34

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 shrink-0">
        <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90" role="img" aria-label={`${label}: ${Math.round(percent)} per cent${target ? `, against a target of ${Math.round(target)} per cent` : ''}`}>
          <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="8" className="text-dpl-border" />
          <circle
            cx="40"
            cy="40"
            r="34"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            className={tone === 'success' ? 'text-dpl-success' : 'text-dpl-danger'}
            strokeDasharray={`${(clamped / 100) * circumference} ${circumference}`}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold tabular-nums text-dpl-text">
          {Math.round(percent)}%
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-dpl-text">{label}</p>
        {caption && <div className="mt-0.5 text-xs text-dpl-muted">{caption}</div>}
      </div>
    </div>
  )
}
